/* ThirdPartyDeliverableSection — Self-Plan only.
 *
 * Captures the three "where did this plan come from?" deliverable fields:
 *   · Planning software used (required dropdown — ClinCheck / 3Shape /
 *     exocad / Dental Wings / Maestro 3D / OnyxCeph / Other)
 *   · Required Plan Viewer Link (URL the lab + Invisique QA replay)
 *   · Optional Plan Walkthrough Video (uploaded as `kind=plan_video`
 *     via /api/files/upload).
 *
 * iter98.99s — Version number field removed per user direction
 * ("don't make the form bulky"). Lives in the main Scans accordion
 * stack as its own collapsible card alongside the other plan-source
 * records (after X-rays).
 *
 * State is owned by the wizard (`planTimeline`); we only update via
 * `updPlan(key, value)` so the caller controls auto-save semantics.
 */
import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SOFTWARE_OPTIONS = [
    "ClinCheck",
    "3Shape",
    "exocad",
    "Dental Wings",
    "Maestro 3D",
    "OnyxCeph",
    "Other",
];

export default function ThirdPartyDeliverableSection({ planTimeline, updPlan, caseId }) {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const onPickVideo = async (file) => {
        if (!file) return;
        if (!caseId) {
            toast.error("Save the case first — pick a patient on Step 1 so the video can be attached.");
            return;
        }
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        try {
            // iter98.99s — Correct endpoint is /cases/{id}/upload?kind=plan_video
            // (case-scoped). The previous code POSTed to /files/upload which
            // doesn't exist and returned 405 Method Not Allowed.
            const r = await api.post(`/cases/${caseId}/upload?kind=plan_video`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            updPlan("video_file_id", r.data.id);
            toast.success("Walkthrough video uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removeVideo = async () => {
        if (!planTimeline.video_file_id) return;
        try {
            await api.delete(`/files/${planTimeline.video_file_id}`);
        } catch (err) {
            console.debug("remove video skipped:", err);
        }
        updPlan("video_file_id", null);
    };

    const sw = planTimeline.planning_software || "";
    const isOther = sw === "Other";
    const hasSoftware = !!sw && (!isOther || (planTimeline.planning_software_other || "").trim().length > 0);
    const hasLink = !!(planTimeline.external_link || "").trim();
    const hasVideo = !!planTimeline.video_file_id;

    return (
        <div className="space-y-3.5" data-testid="case-wizard-self-plan-deliverable">
            {/* Planning Software Used (required dropdown) */}
            <div>
                <Label className="text-xs font-semibold flex items-center gap-1">
                    Planning software used
                    <span className="text-rose-600" aria-hidden="true">*</span>
                    <span className="sr-only">required</span>
                </Label>
                <select
                    value={sw}
                    onChange={(e) => {
                        const next = e.target.value;
                        updPlan("planning_software", next);
                        // iter98.99t — Clear the custom-name field when
                        // switching away from "Other" so it doesn't leak
                        // stale text into the payload.
                        if (next !== "Other" && planTimeline.planning_software_other) {
                            updPlan("planning_software_other", "");
                        }
                    }}
                    className="h-9 mt-1 text-sm w-full rounded-md border border-input bg-card px-3 py-1 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="case-wizard-planning-software"
                >
                    <option value="">Select your planning tool…</option>
                    {SOFTWARE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                {!hasSoftware && (
                    <p className="mt-1 text-[10.5px] text-rose-600/90" data-testid="case-wizard-planning-software-missing">
                        {isOther
                            ? "Type the name of the planning tool you used."
                            : "Required — declares which tool created this plan."}
                    </p>
                )}
            </div>

            {/* iter98.99t — Custom name input when user picks "Other". */}
            {isOther && (
                <div data-testid="case-wizard-planning-software-other-block">
                    <Label className="text-xs font-semibold flex items-center gap-1">
                        Tool name
                        <span className="text-rose-600" aria-hidden="true">*</span>
                        <span className="sr-only">required</span>
                    </Label>
                    <Input
                        type="text"
                        value={planTimeline.planning_software_other || ""}
                        onChange={(e) => updPlan("planning_software_other", e.target.value)}
                        placeholder="e.g. SureSmile / Spark / Angel / in-house tool…"
                        className="h-9 mt-1 text-sm"
                        data-testid="case-wizard-planning-software-other"
                        autoFocus
                    />
                </div>
            )}

            {/* Plan Viewer Link (required) */}
            <div>
                <Label className="text-xs font-semibold flex items-center gap-1">
                    Plan viewer link
                    <span className="text-rose-600" aria-hidden="true">*</span>
                    <span className="sr-only">required</span>
                </Label>
                <Input
                    type="url"
                    value={planTimeline.external_link || ""}
                    onChange={(e) => updPlan("external_link", e.target.value)}
                    placeholder="https://my.aligner-tool/cases/123/setup"
                    className="h-9 mt-1 text-sm"
                    data-testid="case-wizard-plan-external-link"
                />
                {!hasLink && (
                    <p className="mt-1 text-[10.5px] text-rose-600/90" data-testid="case-wizard-plan-external-link-missing">
                        Required — paste the live viewer URL from your software.
                    </p>
                )}
            </div>

            {/* Plan Walkthrough Video (optional) */}
            <div>
                <Label className="text-xs font-semibold">
                    Plan walkthrough video <span className="text-muted-foreground font-normal">(optional · max 500 MB)</span>
                </Label>
                {hasVideo ? (
                    <div className="mt-1 flex items-center justify-between bg-white border border-violet-200 rounded-lg px-3 py-2 text-sm">
                        <span className="text-violet-800 inline-flex items-center gap-1.5 truncate" data-testid="case-wizard-plan-video-attached">
                            Walkthrough video attached
                        </span>
                        <button
                            type="button"
                            onClick={removeVideo}
                            className="text-rose-600 hover:text-rose-700 ml-3 text-xs"
                            aria-label="Detach video"
                            data-testid="case-wizard-plan-video-remove"
                        >
                            Remove
                        </button>
                    </div>
                ) : (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || !caseId}
                        className="mt-1"
                        data-testid="case-wizard-plan-video-upload"
                    >
                        {uploading ? "Uploading…" : "Upload video"}
                    </Button>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={(e) => onPickVideo(e.target.files?.[0])}
                />
                {!caseId && (
                    <p className="mt-1 text-[10.5px] text-muted-foreground italic">
                        Pick a patient on Step 1 to enable video upload.
                    </p>
                )}
            </div>
        </div>
    );
}
